import type { PromptScene } from './prompt-showcase-content'

/** Milliseconds per typed / erased character, and the pauses between. */
const TYPE_MS = 38
const ERASE_MS = 16
/** A typed prompt settles for a beat before its emails rise. */
const RISE_DELAY_MS = 240
/** How long a finished scene stays up before the prompt is erased. */
const HOLD_MS = 3600
/** The empty box between one prompt's erase and the next one's typing. */
const GAP_MS = 450

type Pending = { step: () => void; delay: number }

/**
 * Types each scene's prompt into the composer, raises its emails (the
 * panel's `data-scene`, read by `SceneEmails`), holds, erases, and moves on.
 * Attach it as the composer form's ref callback; it returns its own
 * teardown. It never starts under reduced motion or without a panel
 * (`[data-prompt-stage]`), pauses while the panel is off screen, loads a
 * scene's email images only as it starts typing that scene, and stops for
 * good the moment the visitor reaches for the box.
 */
export function runPromptDemo(
  form: HTMLFormElement,
  scenes: ReadonlyArray<PromptScene>
): () => void {
  const stage = form.closest<HTMLElement>('[data-prompt-stage]')
  const field = form.querySelector('textarea')
  const prefersStill =
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? true
  if (
    !(stage && field) ||
    scenes.length < 2 ||
    prefersStill ||
    typeof IntersectionObserver === 'undefined'
  ) {
    return () => undefined
  }

  let index = 0
  let timer = 0
  let pending: Pending | null = null
  let isVisible = false
  let hasStarted = false
  let isStopped = false

  const scene = () => scenes[index] ?? scenes[0]
  const showScene = (id: string | null) => {
    if (id) {
      stage.dataset.scene = id
    } else {
      delete stage.dataset.scene
    }
  }

  // Later scenes ship their image sources in `data-*` (see `SceneEmails`),
  // so only a scene about to be typed ever downloads its emails.
  const arm = (id: string | undefined) => {
    if (!id) {
      return
    }
    for (const image of stage.querySelectorAll<HTMLImageElement>(
      `[data-scene-row="${id}"] img[data-src]`
    )) {
      if (image.dataset.srcset) {
        image.srcset = image.dataset.srcset
      }
      image.src = image.dataset.src ?? ''
      delete image.dataset.src
      delete image.dataset.srcset
    }
  }

  const schedule = (step: () => void, delay: number) => {
    pending = { step, delay }
    if (!isVisible || isStopped) {
      return
    }
    timer = window.setTimeout(() => {
      pending = null
      step()
    }, delay)
  }

  const type = () => {
    const prompt = scene()?.prompt ?? ''
    field.value = prompt.slice(0, field.value.length + 1)
    if (field.value.length < prompt.length) {
      schedule(type, TYPE_MS)
      return
    }
    schedule(() => {
      showScene(scene()?.id ?? null)
      schedule(erase, HOLD_MS)
    }, RISE_DELAY_MS)
  }

  const erase = () => {
    showScene(null)
    field.value = field.value.slice(0, -1)
    if (field.value.length > 0) {
      schedule(erase, ERASE_MS)
      return
    }
    index = (index + 1) % scenes.length
    arm(scene()?.id)
    schedule(type, GAP_MS)
  }

  // Reaching for the box ends the demo. A half-typed or half-erased prompt
  // is completed (with its emails) so what the visitor sends is whole; an
  // empty box, between scenes, stays empty for their own prompt.
  const stop = () => {
    if (isStopped) {
      return
    }
    isStopped = true
    window.clearTimeout(timer)
    if (field.value.length === 0) {
      showScene(null)
      return
    }
    field.value = scene()?.prompt ?? field.value
    arm(scene()?.id)
    showScene(scene()?.id ?? null)
  }

  const viewport = new IntersectionObserver(
    ([entry]) => {
      isVisible = entry?.isIntersecting ?? false
      if (!hasStarted) {
        hasStarted = true
        // Off screen at mount: start from an empty box so the visitor sees
        // the first prompt typed. On screen: hold what the page rendered.
        if (isVisible) {
          pending = { step: erase, delay: HOLD_MS }
        } else {
          field.value = ''
          showScene(null)
          pending = { step: type, delay: GAP_MS }
        }
      }
      window.clearTimeout(timer)
      if (isVisible && pending) {
        schedule(pending.step, pending.delay)
      }
    },
    { threshold: 0.35 }
  )
  viewport.observe(stage)

  const intents = ['pointerdown', 'focusin', 'keydown'] as const
  for (const intent of intents) {
    form.addEventListener(intent, stop)
  }

  return () => {
    window.clearTimeout(timer)
    viewport.disconnect()
    for (const intent of intents) {
      form.removeEventListener(intent, stop)
    }
  }
}
